import { generateUUID } from '../common/uuid';
import { apiV1Paths } from '../../generated/apiV1Client';
import { clearOfflineAccountData } from './offlinePersistence';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

export const AUTH_TOKEN_KEY = 'mindmappi_auth_token';
export const AUTH_META_KEY = 'mindMapAuthMeta';
export const AUTH_CLEARED_EVENT = 'mindmappi:authCleared';
export const OAUTH_RETURN_URL_KEY = 'mindmappi_oauth_return_url';

const SHARE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const INVITATION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export class UpgradeRequiredError extends Error {
  constructor(payload, fallback = 'This action needs Pro.') {
    super(payload?.error || fallback);
    this.name = 'UpgradeRequiredError';
    this.code = payload?.code || null;
    this.upgradeContext = payload?.upgradeContext || null;
  }
}

async function apiError(res, fallback) {
  try {
    const payload = await res.json();
    if (payload?.upgradeContext?.capabilityKey) {
      return new UpgradeRequiredError(payload, fallback);
    }
    if (payload && typeof payload.error === 'string') {
      return new Error(payload.error);
    }
  } catch {
    // Fall back to the stable client message below.
  }
  return new Error(`${fallback}: ${res.status}`);
}

async function nodeDiscussionError(res, fallback) {
  try {
    const payload = await res.json();
    const error = new Error(payload?.error || fallback);
    error.code = payload?.code || null;
    error.status = res.status;
    error.retryAfterSeconds = Number(payload?.retryAfterSeconds || 0) || null;
    return error;
  } catch {
    const error = new Error(`${fallback}: ${res.status}`);
    error.status = res.status;
    return error;
  }
}

export function getAuthToken() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getShareToken() {
  if (typeof window === 'undefined') return null;
  try {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = hashParams.get('share');
    return token && SHARE_TOKEN_PATTERN.test(token) ? token : null;
  } catch {
    return null;
  }
}

export function getInvitationToken() {
  if (typeof window === 'undefined') return null;
  try {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = hashParams.get('invite');
    return token && INVITATION_TOKEN_PATTERN.test(token) ? token : null;
  } catch {
    return null;
  }
}

export function buildCollaborationCredential(authToken, shareToken = getShareToken()) {
  if (!shareToken) return authToken || undefined;
  return JSON.stringify({ bearerToken: authToken || null, shareToken });
}

function clearCachedSnapshots(ownerUserId) {
  if (!ownerUserId) return;
  try {
    const prefix = `mindMap_${ownerUserId}_`;
    const indexKey = `mindMapIndex_${ownerUserId}`;
    const keysToRemove = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (key === indexKey || key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore cache cleanup errors
  }
}

export function clearStoredAuth(reason = 'unknown') {
  if (typeof window === 'undefined') return;
  try {
    let ownerUserId = null;
    try {
      const metaRaw = window.localStorage.getItem(AUTH_META_KEY);
      const meta = metaRaw ? JSON.parse(metaRaw) : null;
      ownerUserId = meta && meta.ownerUserId ? meta.ownerUserId : null;
    } catch {
      ownerUserId = null;
    }

    clearCachedSnapshots(ownerUserId);
    clearOfflineAccountData(ownerUserId);
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_META_KEY);
  } catch {
    // ignore storage errors
  }

  try {
    window.dispatchEvent(
      new CustomEvent(AUTH_CLEARED_EVENT, {
        detail: { reason },
      })
    );
  } catch {
    // ignore dispatch errors
  }
}

async function authFetch(path, options = {}, includeShareToken = false) {
  const token = getAuthToken();
  const headers = {
    ...(options.headers || {}),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeShareToken) {
    const shareToken = getShareToken();
    if (shareToken) {
      headers['X-Mindmapi-Share-Token'] = shareToken;
    }
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });
  if (res.status === 401 && token) {
    clearStoredAuth('expired');
  }
  return res;
}

export async function getHealth() {
  const res = await fetch(`${API_BASE_URL}/health`);
  if (!res.ok) {
    throw new Error(`Health check failed with status ${res.status}`);
  }
  return res.json();
}

export async function getDbHealth() {
  const res = await fetch(`${API_BASE_URL}/health/db`);
  if (!res.ok) {
    throw new Error(`DB health check failed with status ${res.status}`);
  }
  return res.json();
}

export async function exchangeGoogleOAuthCode(code) {
  const res = await fetch(`${API_BASE_URL}/auth/oauth/google/exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    throw new Error(`Google sign-in exchange failed with status ${res.status}`);
  }
  const payload = await res.json();
  if (!payload || typeof payload.token !== 'string' || !payload.token) {
    throw new Error('Google sign-in exchange returned no token');
  }
  return payload.token;
}

export async function getMyMindMap() {
  const res = await authFetch(apiV1Paths.getMyMindMap());
  if (res.status === 401 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to load mind map: ${res.status}`);
  }
  return res.json();
}

export async function saveMyMindMap(mapData) {
  const res = await authFetch(apiV1Paths.saveMyMindMap(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mapData),
  });

  if (res.status === 401) {
    return null;
  }

  if (!res.ok) {
    throw new Error(`Failed to save mind map: ${res.status}`);
  }

  return res.json();
}

export async function getMindMapById(id) {
  const res = await authFetch(apiV1Paths.getMindMapById(id), {}, true);
  if (res.status === 401 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to load mind map by id: ${res.status}`);
  }
  return res.json();
}

export async function createBranchMap(sourceMapId, graph) {
  const res = await authFetch(apiV1Paths.createBranchMap(sourceMapId), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ graph }),
  });
  if (!res.ok) throw await apiError(res, 'Could not create the branch map');
  return res.json();
}

export async function createMindMap(title) {
  const body = title && title.trim() ? { title: title.trim() } : {};
  const res = await authFetch(apiV1Paths.createMindMap(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    return null;
  }
  if (!res.ok) {
    throw await apiError(res, 'Failed to create mind map');
  }
  return res.json();
}

export async function listMindMaps() {
  const res = await authFetch(apiV1Paths.listMindMaps());
  if (res.status === 401) {
    return [];
  }
  if (!res.ok) {
    throw new Error(`Failed to list mind maps: ${res.status}`);
  }
  return res.json();
}

export async function getAccountCapabilities() {
  const res = await authFetch(apiV1Paths.getAccountCapabilities());
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new Error(`Failed to load account capabilities: ${res.status}`);
  }
  return res.json();
}

export async function getPlatformDashboard(period = '7d') {
  const query = new URLSearchParams({ period }).toString();
  const res = await authFetch(`${apiV1Paths.getPlatformDashboard()}?${query}`);
  if (!res.ok) {
    throw await apiError(res, 'Failed to load the beta dashboard');
  }
  return res.json();
}

export async function updatePlatformSettings(configuration) {
  const res = await authFetch(apiV1Paths.updatePlatformSettings(), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(configuration),
  });
  if (!res.ok) {
    throw await apiError(res, 'Failed to update the beta configuration');
  }
  return res.json();
}

export async function getAccountUpgradeContext(capabilityKey = null) {
  const query = capabilityKey
    ? `?${new URLSearchParams({ capability: capabilityKey }).toString()}`
    : '';
  const res = await authFetch(`${apiV1Paths.getAccountUpgradeContext()}${query}`);
  if (res.status === 401) return null;
  if (!res.ok) {
    throw await apiError(res, 'Failed to load plan details');
  }
  return res.json();
}

export async function activateBetaPro() {
  const res = await authFetch(apiV1Paths.activateBetaPro(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    throw await apiError(res, 'Failed to activate Beta Pro');
  }
  return res.json();
}

export async function acknowledgeMindMapNotifications() {
  const res = await authFetch(apiV1Paths.acknowledgeMindMapNotifications(), {
    method: 'POST',
  });
  if (!res.ok) {
    throw new Error(`Failed to acknowledge map notifications: ${res.status}`);
  }
  return res.json();
}

export async function saveMindMapLink(id) {
  const res = await authFetch(apiV1Paths.saveMindMapLink(id), {
    method: 'POST',
  }, true);
  if (!res.ok) {
    throw new Error(await sharingError(res, 'Failed to save map link'));
  }
  return res.json();
}

export async function removeSavedMindMapLink(id) {
  const res = await authFetch(apiV1Paths.removeSavedMindMapLink(id), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await sharingError(res, 'Failed to remove saved map'));
  }
  return res.json();
}

export async function deleteMindMap(id) {
  const res = await authFetch(apiV1Paths.deleteMindMap(id), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await sharingError(res, 'Failed to delete map'));
  }
  return res.json();
}

export async function updateMindMapArchive(id, archived) {
  const res = await authFetch(apiV1Paths.updateMindMapArchive(id), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ archived }),
  });
  if (!res.ok) {
    throw await apiError(
      res,
      archived ? 'Failed to deactivate map' : 'Failed to activate map'
    );
  }
  return res.json();
}

export async function saveMindMapById(id, mapData) {
  const res = await authFetch(apiV1Paths.saveMindMapById(id), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mapData),
  }, true);

  if (res.status === 401 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to save mind map by id: ${res.status}`);
  }
  return res.json();
}

export async function updateMindMapTitle(id, title) {
  const body = { title: title && title.trim() ? title.trim() : null };
  const res = await authFetch(apiV1Paths.updateMindMapTitle(id), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to update mind map title: ${res.status}`);
  }
  return res.json();
}

export async function updateMindMapDescription(id, description) {
  const body = {
    description: description && description.trim() ? description : null,
  };
  const res = await authFetch(apiV1Paths.updateMindMapDescription(id), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to update mind map description: ${res.status}`);
  }
  return res.json();
}

export async function fetchUrlPreview(url) {
  const params = new URLSearchParams({ url });

  try {
    const res = await authFetch(`/url-preview?${params.toString()}`);

    // Network reached the backend but it responded with an error
    if (!res.ok) {
      throw new Error(`Failed to fetch URL preview: ${res.status}`);
    }

    return res.json();
  } catch (err) {
    // If the request failed at the network layer (e.g. CORS, offline,
    // backend not reachable), treat it as a soft failure and return a
    // minimal fallback object instead of propagating the error. URL
    // previews are non-critical and the rest of the app should keep
    // working even if they fail.
    console.error('URL preview request failed', err);
    return {
      url,
      title: null,
      description: null,
      image: null,
      siteName: null,
    };
  }
}

export async function uploadMindMapThumbnail(id, dataUrlOrBase64) {
  const payload = {};
  if (typeof dataUrlOrBase64 === 'string') {
    if (dataUrlOrBase64.startsWith('data:image/')) {
      payload.dataUrl = dataUrlOrBase64;
    } else {
      payload.base64 = dataUrlOrBase64;
    }
  } else {
    throw new Error('Invalid thumbnail payload');
  }

  const res = await authFetch(apiV1Paths.uploadMindMapThumbnail(id), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (res.status === 401 || res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`Failed to upload mind map thumbnail: ${res.status}`);
  }
  return res.json();
}

export async function uploadNodeAsset(id, file, kind) {
  const query = new URLSearchParams({ filename: file.name, kind });
  const inferredImageType = /\.png$/i.test(file.name)
    ? 'image/png'
    : /\.webp$/i.test(file.name)
      ? 'image/webp'
      : /\.svg$/i.test(file.name)
        ? 'image/svg+xml'
      : /\.jpe?g$/i.test(file.name)
        ? 'image/jpeg'
        : 'application/octet-stream';
  const contentType = file.type || (kind === 'markdown' ? 'text/markdown' : kind === 'pdf' ? 'application/pdf' : inferredImageType);
  const res = await authFetch(`${apiV1Paths.uploadNodeAsset(id)}?${query}`, {
    method: 'POST',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!res.ok) {
    throw await apiError(res, 'Could not upload this attachment');
  }
  return (await res.json()).asset;
}

export async function deleteNodeAsset(id, assetId) {
  const res = await authFetch(apiV1Paths.deleteNodeAsset(id, assetId), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw await apiError(res, 'Could not remove this attachment');
  }
  return true;
}

export async function fetchNodeAssetContent(id, assetId, variant = 'display') {
  const query = new URLSearchParams({ variant });
  const res = await authFetch(
    `${apiV1Paths.getNodeAssetContent(id, assetId)}?${query}`,
    { cache: 'no-store' },
    true
  );
  if (!res.ok) {
    throw await apiError(res, 'Could not load this attachment');
  }
  return res.blob();
}

export async function updateMarkdownNodeAssetContent(id, assetId, content, filename = null) {
  const query = filename ? `?${new URLSearchParams({ filename })}` : '';
  const res = await authFetch(`${apiV1Paths.updateMarkdownNodeAssetContent(id, assetId)}${query}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'text/markdown; charset=utf-8' },
    body: String(content ?? ''),
  });
  if (!res.ok) {
    throw await apiError(res, 'Could not save this Markdown file');
  }
  return (await res.json()).asset;
}

async function sharingError(res, fallback) {
  try {
    const payload = await res.json();
    if (payload && typeof payload.error === 'string') return payload.error;
  } catch {
    // Fall back to the stable client message below.
  }
  return `${fallback}: ${res.status}`;
}

export async function getMindMapSharing(id) {
  const res = await authFetch(apiV1Paths.getMindMapSharing(id));
  if (!res.ok) {
    throw new Error(await sharingError(res, 'Failed to load sharing settings'));
  }
  return res.json();
}

async function aiMapSharingError(res, fallback) {
  try {
    const payload = await res.json();
    const error = new Error(payload?.error || fallback);
    error.code = payload?.code || null;
    error.status = res.status;
    return error;
  } catch {
    const error = new Error(`${fallback}: ${res.status}`);
    error.status = res.status;
    return error;
  }
}

export async function getAiMapShares(id) {
  const res = await authFetch(`/api/v1/mindmaps/${encodeURIComponent(id)}/ai-shares`);
  if (res.status === 404) return { available: false, grants: [] };
  if (!res.ok) throw await aiMapSharingError(res, 'Failed to load AI shares');
  return { available: true, ...(await res.json()) };
}

export async function createAiMapShare(id, scopeRootId = null, allowEditing = false, includeSurroundingContext = false) {
  const res = await authFetch(`/api/v1/mindmaps/${encodeURIComponent(id)}/ai-shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scopeRootId, allowEditing, includeSurroundingContext }),
  });
  if (!res.ok) throw await aiMapSharingError(res, 'Failed to create AI share');
  return res.json();
}

export async function getAiMapShareDetails(id, grantId) {
  const res = await authFetch(`/api/v1/mindmaps/${encodeURIComponent(id)}/ai-shares/${encodeURIComponent(grantId)}`);
  if (!res.ok) throw await aiMapSharingError(res, 'This snapshot is no longer available. Create a new share.');
  return res.json();
}

export async function revokeAiMapShare(id, grantId) {
  const res = await authFetch(
    `/api/v1/mindmaps/${encodeURIComponent(id)}/ai-shares/${encodeURIComponent(grantId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw await aiMapSharingError(res, 'Failed to revoke AI share');
  return res.json();
}

export async function upsertMindMapMember(id, email, role, focusNodeId = null) {
  const res = await authFetch(apiV1Paths.upsertMindMapMember(id), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role, focusNodeId }),
  });
  if (!res.ok) {
    throw await apiError(res, 'Failed to update collaborator');
  }
  return res.json();
}

export async function removeMindMapMember(id, userId) {
  const res = await authFetch(apiV1Paths.removeMindMapMember(id, userId), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await sharingError(res, 'Failed to remove collaborator'));
  }
  return res.json();
}

export async function revokeMindMapInvitation(id, invitationId) {
  const res = await authFetch(apiV1Paths.revokeMindMapInvitation(id, invitationId), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error(await sharingError(res, 'Failed to revoke invitation'));
  }
  return res.json();
}

export async function acceptMindMapInvitation(token) {
  const res = await authFetch(apiV1Paths.acceptMindMapInvitation(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    throw await apiError(res, 'Failed to accept map invitation');
  }
  return res.json();
}

export async function updateMindMapLinkAccess(id, access, rotate = false) {
  const res = await authFetch(apiV1Paths.updateMindMapLinkAccess(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access, rotate }),
  });
  if (!res.ok) {
    throw await apiError(res, 'Failed to update link access');
  }
  return res.json();
}

export async function listModeratedBranches(id) {
  const res = await authFetch(apiV1Paths.listModeratedBranches(id), {}, true);
  if (!res.ok) {
    throw await apiError(res, 'Could not load branch moderation');
  }
  return res.json();
}

export async function hideMindMapBranch(id, nodeId, creatorUserId) {
  const res = await authFetch(apiV1Paths.hideMindMapBranch(id, nodeId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ creatorUserId }),
  });
  if (!res.ok) {
    throw await apiError(res, 'Could not hide this branch');
  }
  return res.json();
}

export async function restoreMindMapBranch(id, nodeId) {
  const res = await authFetch(apiV1Paths.restoreMindMapBranch(id, nodeId), {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw await apiError(res, 'Could not restore this branch');
  }
  return res.json();
}

export async function listNodeDiscussions(id, nodeIds) {
  const boundedNodeIds = [...new Set((nodeIds || []).filter(Boolean))].slice(0, 100);
  if (!id || boundedNodeIds.length === 0) {
    return { discussions: [] };
  }
  const query = new URLSearchParams({ nodeIds: boundedNodeIds.join(',') });
  const res = await authFetch(`${apiV1Paths.listNodeDiscussions(id)}?${query}`);
  if (!res.ok) {
    throw await nodeDiscussionError(res, 'Could not load idea discussions');
  }
  return res.json();
}

export async function getNodeDiscussion(id, nodeId, options = {}) {
  const query = new URLSearchParams();
  if (options.beforeSequence) {
    query.set('beforeSequence', String(options.beforeSequence));
  }
  query.set('limit', String(options.limit || 50));
  const res = await authFetch(
    `${apiV1Paths.getNodeDiscussion(id, nodeId)}?${query}`
  );
  if (!res.ok) {
    throw await nodeDiscussionError(res, 'Could not load this discussion');
  }
  return res.json();
}

export async function createNodeDiscussionMessage(
  id,
  nodeId,
  body,
  clientMessageId = generateUUID()
) {
  const res = await authFetch(apiV1Paths.createNodeDiscussionMessage(id, nodeId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientMessageId, body }),
  });
  if (!res.ok) {
    throw await nodeDiscussionError(res, 'Could not send this message');
  }
  return res.json();
}

export async function markNodeDiscussionRead(
  id,
  nodeId,
  lastReadSequence,
  interactionId = null
) {
  const res = await authFetch(apiV1Paths.markNodeDiscussionRead(id, nodeId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lastReadSequence,
      ...(interactionId ? { interactionId } : {}),
    }),
  });
  if (!res.ok) {
    throw await nodeDiscussionError(res, 'Could not update discussion read state');
  }
  return res.json();
}

export async function getAiMapEdits(id) {
  const res = await authFetch(`/api/v1/mindmaps/${encodeURIComponent(id)}/ai-edits`);
  if (!res.ok) throw await aiMapSharingError(res, 'AI edit request failed');
  return res.json();
}
export async function undoAiMapEdit(id, batchId) {
  const res = await authFetch(`/api/v1/mindmaps/${encodeURIComponent(id)}/ai-edits/${encodeURIComponent(batchId)}/undo`, { method: 'POST' });
  if (!res.ok) throw await aiMapSharingError(res, 'AI edit request failed');
  return res.json();
}

export async function searchMindMaps(query, { cursor, signal } = {}) {
  const params = new URLSearchParams({ q: query });
  if (cursor) params.set('cursor', cursor);
  const response = await authFetch(`${apiV1Paths.searchMindMaps()}?${params}`, { signal });
  if (!response.ok) throw await apiError(response, 'Could not search maps');
  return response.json();
}

export async function getCollaboratorProfiles(mapId, userIds, { signal } = {}) {
  const response = await authFetch(apiV1Paths.getCollaboratorProfiles(mapId), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, signal,
    body: JSON.stringify({ userIds }),
  }, true);
  if (!response.ok) throw await apiError(response, 'Could not load collaborator pictures');
  return response.json();
}
